import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Empty, Flex, List, Progress, Spin, Tag, Typography } from '@/shared/antd-imports';
import clientPortalAPI from '@/services/api';

interface IOverviewRow {
  project_id: string;
  project_name: string;
  open: number;
  total: number;
}

/** Portal → "What we need from you": every project with items the provider asked for, open ones first. */
const InputsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IOverviewRow[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [hasError, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await clientPortalAPI.getInputsOverview();
      setRows(response.body?.projects ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Flex vertical gap={16}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        {t('inputs.title', { defaultValue: 'What we need from you' })}
      </Typography.Title>
      {hasError && (
        <Alert
          type="error"
          showIcon
          message={t('inputs.loadError', { defaultValue: 'Could not load the checklist. Please try again.' })}
          action={<Button size="small" onClick={load}>{t('inputs.retry', { defaultValue: 'Try again' })}</Button>}
        />
      )}
      {!hasError && rows.length === 0 && (
        <Card>
          <Empty description={t('inputs.empty', { defaultValue: 'Nothing is needed from you right now.' })} />
        </Card>
      )}
      {rows.length > 0 && (
        <Card styles={{ body: { padding: 0 } }}>
          <List
            dataSource={rows}
            renderItem={row => (
              <List.Item
                style={{ padding: '12px 16px', cursor: 'pointer' }}
                onClick={() => navigate(`/projects/${row.project_id}/inputs`)}
                actions={[
                  <Button key="open" type={row.open > 0 ? 'primary' : 'default'} onClick={() => navigate(`/projects/${row.project_id}/inputs`)}>
                    {row.open > 0 ? t('inputs.open', { defaultValue: 'Complete' }) : t('inputs.view', { defaultValue: 'View' })}
                  </Button>,
                ]}
              >
                <Flex vertical gap={4} style={{ minWidth: 0, flex: 1 }}>
                  <Typography.Text strong>{row.project_name}</Typography.Text>
                  <Flex gap={8} align="center" wrap>
                    {row.open > 0 ? (
                      <Tag color="warning">{t('inputs.remaining', { defaultValue: '{{count}} item(s) left', count: row.open })}</Tag>
                    ) : (
                      <Tag color="success">{t('inputs.allDone', { defaultValue: 'All done, thank you!' })}</Tag>
                    )}
                    <Progress percent={Math.round(((row.total - row.open) / Math.max(row.total, 1)) * 100)} size="small" style={{ width: 140, margin: 0 }} />
                  </Flex>
                </Flex>
              </List.Item>
            )}
          />
        </Card>
      )}
    </Flex>
  );
};

export default InputsPage;
